/**
 * Utility functions for TerrainLayer and subclasses
 * Includes: debounce, coordinate transformations, tile calculations, interpolation
 */

/**
 * Debounce utility - delay function execution until after specified wait time
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export function debounce (func, wait) {
    let timeout;
    return function executedFunction (...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

/**
 * Convert longitude/latitude to Web Mercator tile coordinates
 * @param {Object|Array} lngLat - Geographic coordinates {lng, lat} or [lng, lat]
 * @param {number} z - Zoom level
 * @returns {Object} Tile coordinates {x, y}
 */
export function lngLatToTile (lngLat, z) {
    let lng;
    if (lngLat.lng !== null && typeof lngLat.lng === 'number') {
        lng = lngLat.lng;
    } else if (lngLat.lon !== null && typeof lngLat.lon === 'number') {
        lng = lngLat.lon;
    } else if (Array.isArray(lngLat) && typeof lngLat[0] === 'number') {
        lng = lngLat[0];
    } else {
        console.warn('[lngLatToTile] Could not extract lng:', lngLat);
        return { x: 0, y: 0 };
    }

    let lat;
    if (lngLat.lat !== null && typeof lngLat.lat === 'number') {
        lat = lngLat.lat;
    } else if (Array.isArray(lngLat) && typeof lngLat[1] === 'number') {
        lat = lngLat[1];
    } else {
        console.warn('[lngLatToTile] Could not extract lat:', lngLat);
        return { x: 0, y: 0 };
    }

    // Calculate tile coordinates using Web Mercator projection
    const n = Math.pow(2, z);
    const x = (lng + 180) / 360 * n;
    const latRad = (lat * Math.PI) / 180;
    const y = (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n;

    return { x: Math.floor(x), y: Math.floor(y) };
}

/**
 * Get tile corner coordinates in lng/lat
 * @param {number} z - Zoom level
 * @param {number} x - Tile X coordinate
 * @param {number} y - Tile Y coordinate
 * @returns {Object} Object with nwLng, nwLat, neLng, neLat, seLng, seLat, swLng, swLat
 */
export function getTileCoordinates (z, x, y) {
    const n = Math.pow(2, z);
    const nwLng = (x / n) * 360 - 180;
    const seLng = ((x + 1) / n) * 360 - 180;
    const nwLat = Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n))) * (180 / Math.PI);
    const seLat = Math.atan(Math.sinh(Math.PI * (1 - (2 * (y + 1)) / n))) * (180 / Math.PI);

    return {
        nwLng,
        nwLat,
        neLng: seLng,
        neLat: nwLat,
        seLng,
        seLat,
        swLng: nwLng,
        swLat: seLat,
    };
}

/**
 * Bilinear interpolation - get color value at fractional pixel position
 * @param {number} px - Fractional x position (0-255)
 * @param {number} py - Fractional y position (0-255)
 * @param {Uint8ClampedArray} data - Image data array (RGBA format)
 * @param {number} width - Image width
 * @returns {Array} RGBA color [r, g, b, a]
 */
export function bilinearInterpolateColor (px, py, data, width) {
    const ix = Math.floor(px);
    const iy = Math.floor(py);
    const fx = px - ix;
    const fy = py - iy;
    const c00 = getPixelColor(ix, iy, data, width);
    const c10 = getPixelColor(ix + 1, iy, data, width);
    const c01 = getPixelColor(ix, iy + 1, data, width);
    const c11 = getPixelColor(ix + 1, iy + 1, data, width);
    const c0 = [
        c00[0] * (1 - fx) + c10[0] * fx,
        c00[1] * (1 - fx) + c10[1] * fx,
        c00[2] * (1 - fx) + c10[2] * fx,
        c00[3] * (1 - fx) + c10[3] * fx,
    ];
    const c1 = [
        c01[0] * (1 - fx) + c11[0] * fx,
        c01[1] * (1 - fx) + c11[1] * fx,
        c01[2] * (1 - fx) + c11[2] * fx,
        c01[3] * (1 - fx) + c11[3] * fx,
    ];
    return [
        Math.round(c0[0] * (1 - fy) + c1[0] * fy),
        Math.round(c0[1] * (1 - fy) + c1[1] * fy),
        Math.round(c0[2] * (1 - fy) + c1[2] * fy),
        Math.round(c0[3] * (1 - fy) + c1[3] * fy),
    ];
}

/**
 * Get pixel color at coordinates
 * @param {number} x - X coordinate
 * @param {number} y - Y coordinate
 * @param {Uint8ClampedArray} data - Image data array
 * @param {number} width - Image width
 * @returns {Array} RGBA color [r, g, b, a]
 */
function getPixelColor (x, y, data, width) {
    const index = (Math.floor(y) * width + Math.floor(x)) * 4;
    return [ data[index], data[index + 1], data[index + 2], data[index + 3] ];
}

/**
 * Extract child tile from parent tile using bilinear interpolation
 * @param {ImageData} parentImageData - Parent tile image data
 * @param {number} childIndex - Child index (0-3: NW, NE, SW, SE)
 * @returns {ImageData} Child tile image data (256x256)
 */
export function extractChild (parentImageData, childIndex) {
    const PARENT_SIZE = 512;
    const CHILD_SIZE = 256;
    const scale = PARENT_SIZE / CHILD_SIZE;
    const childCanvas = document.createElement('canvas');
    childCanvas.width = CHILD_SIZE;
    childCanvas.height = CHILD_SIZE;
    const childCtx = childCanvas.getContext('2d');
    const childImageData = childCtx.createImageData(CHILD_SIZE, CHILD_SIZE);
    const childData = childImageData.data;
    const offsetX = (childIndex % 2) * CHILD_SIZE;
    const offsetY = Math.floor(childIndex / 2) * CHILD_SIZE;
    for (let y = 0; y < CHILD_SIZE; y++) {
        for (let x = 0; x < CHILD_SIZE; x++) {
            const px = (offsetX + x) * scale;
            const py = (offsetY + y) * scale;
            const color = bilinearInterpolateColor(px, py, parentImageData.data, PARENT_SIZE);
            const index = (y * CHILD_SIZE + x) * 4;
            childData[index] = color[0];
            childData[index + 1] = color[1];
            childData[index + 2] = color[2];
            childData[index + 3] = color[3];
        }
    }
    return childImageData;
}
